import type { RuleSet, Tiebreaker } from '@airmail/engine';
import { Field, Input, Select, Button } from '@/ui';
import { ArrowDown, ArrowUp } from 'lucide-react';
import { useT } from '@/i18n';

const TB_LABELS: Record<Tiebreaker, string> = {
  points: 'Punkte',
  headToHead: 'Direkter Vergleich',
  scoreDiff: 'Punktdifferenz',
  scoreFor: 'Erzielte Punkte',
  wins: 'Siege',
  lot: 'Los',
};

/** Was „Am Limit" im Einzelnen heißt — als Hinweis unter der Auswahl */
const LIMIT_HINWEIS: Record<RuleSet['onLimit'], string> = {
  suddenDeath: 'Inning zu Ende, dann Sudden Death.',
  leaderWins: 'Inning zu Ende, dann gewinnt der Führende.',
  draw: 'Unentschieden zulassen.',
};

/**
 * Regelwerk-Editor (docs/02 §2) — für Assistent und Einstellungen.
 *
 * Vier Felder nebeneinander gibt es erst ab `lg` (1024 px): Bei ~800 px waren
 * die Auswahlen so schmal, dass „1 Punkt Vorsprun…" und „nur Gruppenphas…"
 * abgeschnitten dastanden. Die Einträge sind darum kurz („1 Punkt", „Sudden
 * Death", „nur Gruppen"); der ganze Wortlaut steht als `title` an der Auswahl
 * und, wo er etwas erklärt, als Hinweis unter dem Feld. `min-w-0` an Feld und
 * Auswahl, sonst zieht der längste Eintrag die Spalte breiter als die Seite.
 */
export function RuleEditor({ rules, onChange, compact }: { rules: RuleSet; onChange: (r: RuleSet) => void; compact?: boolean }) {
  const t = useT();
  const set = <K extends keyof RuleSet>(k: K, v: RuleSet[K]) => onChange({ ...rules, [k]: v });
  const fmt = rules.matchFormat;
  const vier = 'field-row grid grid-cols-2 lg:grid-cols-4 gap-4 sm:items-end';
  return (
    <div className="grid gap-5">
      <div className="field-row grid grid-cols-1 sm:grid-cols-3 gap-4 sm:items-end">
        <Field label={t('Teamgröße')} className="min-w-0">
          <Select value={rules.teamSize} onChange={(e) => set('teamSize', Number(e.target.value) as RuleSet['teamSize'])}>
            <option value={1}>{t('Singles (1)')}</option>
            <option value={2}>{t('Doubles (2)')}</option>
            <option value={3}>{t('3er')}</option>
            <option value={4}>{t('4er')}</option>
          </Select>
        </Field>
        <Field label={t('Match-Modus')} className="min-w-0">
          <Select
            value={fmt.kind === 'single' ? 'single' : fmt.kind === 'bestOf' ? `bestOf${fmt.sets}` : `fixed${fmt.sets}`}
            onChange={(e) => {
              const v = e.target.value;
              set('matchFormat', v === 'single' ? { kind: 'single' } : v === 'bestOf3' ? { kind: 'bestOf', sets: 3 } : v === 'bestOf5' ? { kind: 'bestOf', sets: 5 } : { kind: 'fixedSets', sets: 2 });
            }}
          >
            <option value="single">{t('1 Spiel')}</option>
            <option value="bestOf3">{t('Best-of-3')}</option>
            <option value="bestOf5">{t('Best-of-5')}</option>
            <option value="fixed2">{t('2 feste Spiele')}</option>
          </Select>
        </Field>
        <Field label={t('Ergebnismodus')} className="min-w-0">
          <Select value={rules.resultMode} onChange={(e) => set('resultMode', e.target.value as RuleSet['resultMode'])}>
            <option value="score">{t('Endstand (21:15)')}</option>
            <option value="winnerOnly">{t('Nur Sieger')}</option>
          </Select>
        </Field>
      </div>

      <div className={vier}>
        <Field label={t('Punktziel')} className="min-w-0">
          <Select value={rules.target} onChange={(e) => set('target', Number(e.target.value))}>
            {[11, 15, 21, 25].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t('Sieg mit')} className="min-w-0">
          <Select value={rules.winBy} onChange={(e) => set('winBy', Number(e.target.value) as 1 | 2)} title={t('Sieg mit 1 Punkt oder mit 2 Punkten Vorsprung')}>
            <option value={1}>{t('1 Punkt')}</option>
            <option value={2}>{t('2 Punkte')}</option>
          </Select>
        </Field>
        <Field label={t('Bust (Überschreiten)')} className="min-w-0">
          <Select value={rules.bust === 'off' ? 'off' : String(rules.bust)} onChange={(e) => set('bust', e.target.value === 'off' ? 'off' : Number(e.target.value))}>
            <option value="off">{t('aus')}</option>
            <option value="11">{t('zurück auf 11')}</option>
            <option value="15">{t('zurück auf 15')}</option>
          </Select>
        </Field>
        <Field label={t('Deckel (bei Sieg mit 2)')} className="min-w-0">
          <Input
            type="number"
            min={rules.target}
            value={rules.cap ?? ''}
            placeholder={t('aus')}
            disabled={rules.winBy !== 2}
            onChange={(e) => set('cap', e.target.value ? Number(e.target.value) : undefined)}
          />
        </Field>
      </div>

      <div className={vier}>
        <Field label={t('Max. Innings')} className="min-w-0">
          <Input type="number" min={1} value={rules.maxInnings ?? ''} placeholder={t('unbegrenzt')} onChange={(e) => set('maxInnings', e.target.value ? Number(e.target.value) : undefined)} />
        </Field>
        <Field label={t('Zeitlimit (min)')} className="min-w-0">
          <Input type="number" min={1} value={rules.timeLimitMin ?? ''} placeholder={t('aus')} onChange={(e) => set('timeLimitMin', e.target.value ? Number(e.target.value) : undefined)} />
        </Field>
        {/* Kurze Einträge (auch „Führender gewinnt" war bei vier Spalten zu breit); der Hinweis darunter sagt, was der gewählte bedeutet */}
        <Field label={t('Am Limit')} className="min-w-0" hint={t(LIMIT_HINWEIS[rules.onLimit])}>
          <Select
            value={rules.onLimit}
            onChange={(e) => set('onLimit', e.target.value as RuleSet['onLimit'])}
            title={t('Am Limit: Inning zu Ende, dann Sudden Death — oder Inning zu Ende, Führender gewinnt — oder Unentschieden zulassen')}
          >
            <option value="suddenDeath">{t('Sudden Death')}</option>
            <option value="leaderWins">{t('Führender')}</option>
            <option value="draw">{t('Unentschieden')}</option>
          </Select>
        </Field>
        <Field label={t('Unentschieden')} className="min-w-0">
          <Select value={rules.draws} onChange={(e) => set('draws', e.target.value as RuleSet['draws'])} title={t('Unentschieden nur in der Gruppenphase oder nie')}>
            <option value="groupOnly">{t('nur Gruppen')}</option>
            <option value="never">{t('nie')}</option>
          </Select>
        </Field>
      </div>

      {!compact && (
        <>
          <div className={vier}>
            <Field label={t('Punkte Sieg')} className="min-w-0">
              <Input type="number" value={rules.points.win} onChange={(e) => set('points', { ...rules.points, win: Number(e.target.value) })} />
            </Field>
            <Field label={t('Unentschieden')} className="min-w-0">
              <Input type="number" value={rules.points.draw} onChange={(e) => set('points', { ...rules.points, draw: Number(e.target.value) })} />
            </Field>
            <Field label={t('Geplante Spieldauer (min)')} className="min-w-0">
              <Input type="number" min={1} value={rules.plannedMatchMinutes} onChange={(e) => set('plannedMatchMinutes', Number(e.target.value) || 20)} />
            </Field>
            <Field label={t('Mindestpause je Team (min)')} className="min-w-0">
              <Input type="number" min={0} value={rules.minRestMinutes} onChange={(e) => set('minRestMinutes', Number(e.target.value) || 0)} />
            </Field>
          </div>
          <div>
            <span className="label block mb-1.5">{t('Tiebreaker-Reihenfolge')}</span>
            <ol className="m-0 p-0 list-none grid gap-1 max-w-md">
              {rules.tiebreakers.map((tb, i) => (
                <li key={tb} className="flex items-center gap-2 bg-panel2 border border-line px-3 py-1.5 text-sm">
                  <span className="font-mono text-dim w-4">{i + 1}</span>
                  <span className="flex-1">{t(TB_LABELS[tb])}</span>
                  <Button size="sm" variant="ghost" disabled={i === 0} onClick={() => set('tiebreakers', swap(rules.tiebreakers, i, i - 1))} aria-label={t('Nach oben')}>
                    <ArrowUp size={12} />
                  </Button>
                  <Button size="sm" variant="ghost" disabled={i === rules.tiebreakers.length - 1} onClick={() => set('tiebreakers', swap(rules.tiebreakers, i, i + 1))} aria-label={t('Nach unten')}>
                    <ArrowDown size={12} />
                  </Button>
                </li>
              ))}
            </ol>
          </div>
        </>
      )}
    </div>
  );
}

function swap<T>(arr: readonly T[], i: number, j: number): T[] {
  const out = arr.slice();
  const a = out[i] as T;
  out[i] = out[j] as T;
  out[j] = a;
  return out;
}
